import {useCallback, useEffect, useMemo, useState} from 'react';
import {findMatches, findStemMatches, type SearchMatch, type TextRange} from './transcriptSearch';
import {logTranscriptSearch} from '../insightsLog';

const LOG_DELAY_MS = 800;

export type SearchMode = 'text' | 'stems';

export interface TranscriptSearch {
    query: string;
    mode: SearchMode;
    setQuery: (query: string) => void;
    searchStems: (label: string) => void;
    matches: SearchMatch[];
    current: number;
    currentMatch: SearchMatch | null;
    rangesBySegment: Map<number, TextRange[]>;
    next: () => void;
    previous: () => void;
    clear: () => void;
}

export const useTranscriptSearch = (segments: readonly {text: string}[]): TranscriptSearch => {
    const [query, setQueryState] = useState('');
    const [mode, setMode] = useState<SearchMode>('text');
    const [current, setCurrent] = useState(0);
    const matches = useMemo(
        () => (mode === 'stems' ? findStemMatches(segments, query) : findMatches(segments, query)),
        [segments, query, mode],
    );

    const rangesBySegment = useMemo(() => {
        const map = new Map<number, TextRange[]>();
        for (const match of matches) {
            const list = map.get(match.segmentIndex) ?? [];
            list.push({start: match.start, end: match.end});
            map.set(match.segmentIndex, list);
        }
        return map;
    }, [matches]);

    useEffect(() => {
        if (!query.trim()) return;
        const timer = setTimeout(() => logTranscriptSearch(matches.length), LOG_DELAY_MS);
        return () => clearTimeout(timer);
    }, [query, matches.length]);

    const setQuery = useCallback((value: string) => {
        setQueryState(value);
        setMode('text');
        setCurrent(0);
    }, []);

    const searchStems = useCallback((label: string) => {
        setQueryState(label);
        setMode('stems');
        setCurrent(0);
    }, []);

    const next = useCallback(() => {
        if (matches.length > 0) setCurrent((index) => (index + 1) % matches.length);
    }, [matches.length]);

    const previous = useCallback(() => {
        if (matches.length > 0) setCurrent((index) => (index - 1 + matches.length) % matches.length);
    }, [matches.length]);

    const clear = useCallback(() => setQuery(''), [setQuery]);

    const safeCurrent = matches.length > 0 ? Math.min(current, matches.length - 1) : 0;

    return {
        query,
        mode,
        setQuery,
        searchStems,
        matches,
        current: safeCurrent,
        currentMatch: matches[safeCurrent] ?? null,
        rangesBySegment,
        next,
        previous,
        clear,
    };
};
